import { get, patch, post, type RequestConfig } from "../shared/api-client";
import type { ApiItemResponse, ApiListResponse } from "@project/types/location";
import type {
  DonationDonorPayload,
  DonationDonorRecord,
} from "@project/types/donation-donor";
import {
  JIVANDHARA_BASE,
  SILENT_LIST,
  unwrapItem,
} from "../admin/list-api-helpers";

const BASE = `${JIVANDHARA_BASE}/donation-master`;
const SILENT: RequestConfig = { showNotification: false };

function extractDonorList(
  data:
    | ApiListResponse<DonationDonorRecord>
    | DonationDonorRecord[]
    | { data?: DonationDonorRecord[]; results?: DonationDonorRecord[] }
    | undefined
): DonationDonorRecord[] {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if ("data" in data && Array.isArray(data.data)) {
    return data.data;
  }
  if ("results" in data && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
}

class PublicDonationDonorService {
  searchByPhone = async (
    phone: string
  ): Promise<DonationDonorRecord | null> => {
    try {
      const data = await get<
        | ApiListResponse<DonationDonorRecord>
        | DonationDonorRecord[]
        | { data?: DonationDonorRecord[]; results?: DonationDonorRecord[] }
      >(`${BASE}/`, {
        ...SILENT_LIST,
        params: { search: phone },
      });
      const list = extractDonorList(data);
      return list.length > 0 ? list[0] : null;
    } catch {
      return null;
    }
  };

  create = async (
    payload: DonationDonorPayload
  ): Promise<DonationDonorRecord> => {
    const data = await post<
      ApiItemResponse<DonationDonorRecord> | DonationDonorRecord,
      DonationDonorPayload
    >(`${BASE}/`, payload, SILENT);
    const item = unwrapItem(data);
    if (!item) {
      throw new Error("Failed to create donor record.");
    }
    return item;
  };

  update = async (
    id: number | string,
    payload: Partial<DonationDonorPayload>
  ): Promise<DonationDonorRecord | undefined> => {
    const data = await patch<
      ApiItemResponse<DonationDonorRecord> | DonationDonorRecord,
      Partial<DonationDonorPayload>
    >(`${BASE}/${id}/`, payload, SILENT);
    return unwrapItem(data);
  };
}

export const publicDonationDonorService = new PublicDonationDonorService();
